import { can, CAP } from '@/lib/can'
import type { Capability, CurrentUser, RoleRecord } from '@/types/domain'

/* An account's own grants on the user card (S-7, O2 and ADVISOR B3).
 *
 * The card offers three boxes — the FR-44 directory grants — and binds them to
 * the account's OWN grants (`DirectoryUser.userCapabilities`), never to its
 * effective set. The role's part is shown beside them and owned by the role: a
 * box ticked only because the role grants it would pin the grant on the account
 * and keep it after the role lost it.
 *
 * The draft carries the WHOLE own set, of which the boxes touch three keys, so
 * the save sends (own grants minus those three) ∪ the ticked boxes and never
 * drops an own grant the card does not show. */

/** The three boxes, in the order the list and the card print them. */
export const DIRECTORY_GRANTS = [
  {
    capability: CAP.directoryClientManage,
    labelKey: 'directories.users.grantLabels.manageClients',
  },
  {
    /* The legacy `manageParticipants` grant, which keeps its wire name past
       S-22b: it is `counterparty.manage` now, and the server reaches nothing
       with it unless the account also reads counterparties. */
    capability: CAP.counterpartyManage,
    labelKey: 'directories.users.grantLabels.manageParticipants',
  },
  {
    capability: CAP.directoryTransportManage,
    labelKey: 'directories.users.grantLabels.manageTransport',
  },
] as const

/** How one box renders.
 *
 *  `note` is the visible reason beside a box the role also speaks to. A box the
 *  role alone grants is `disabled`: there is nothing to tick, and unticking it
 *  would change nothing the role does not decide. `refusal` is the authority
 *  rule (S-7, O7): the actor may hand out only what they hold themselves, so a
 *  box for a capability they lack is `disabled` and KEEPS its value — the save
 *  sends the account's grant back as it was, which the server accepts, rather
 *  than revoking something the actor could never restore. */
export interface GrantState {
  checked: boolean
  disabled: boolean
  note?: 'grantedByRole' | 'alsoGrantedByRole'
  refusal?: 'grantNotYours'
}

/** Whether a role grants a capability: its list, or the superuser flag, which
 *  passes every check whatever the list says. An unknown role grants nothing. */
export function roleGrants(role: RoleRecord | undefined, capability: Capability): boolean {
  if (!role) return false
  return role.isSuperuser || role.capabilities.includes(capability)
}

export function grantState(
  capability: Capability,
  own: readonly Capability[],
  role: RoleRecord | undefined,
  actor: CurrentUser | null | undefined,
): GrantState {
  const state = roleState(capability, own, role)
  // `can` passes the superuser and fails closed while the session is unknown.
  return can(actor, capability)
    ? state
    : { ...state, disabled: true, refusal: 'grantNotYours' }
}

function roleState(
  capability: Capability,
  own: readonly Capability[],
  role: RoleRecord | undefined,
): GrantState {
  const byRole = roleGrants(role, capability)
  if (own.includes(capability)) {
    return byRole
      ? { checked: true, disabled: false, note: 'alsoGrantedByRole' }
      : { checked: true, disabled: false }
  }
  if (byRole) return { checked: true, disabled: true, note: 'grantedByRole' }
  return { checked: false, disabled: false }
}

/** The own set in its canonical order — sorted, so ticking a box and
 *  unticking it again gives back the very array the sheet was seeded with, and
 *  `useFormDraft`'s structural `dirty` does not call an undone click an edit. */
export function ownGrants(own: readonly Capability[]): Capability[] {
  return [...new Set(own)].sort()
}

/** The own set with one key ticked or unticked; every other key kept. */
export function withGrant(
  own: readonly Capability[],
  capability: Capability,
  on: boolean,
): Capability[] {
  const rest = own.filter((key) => key !== capability)
  return ownGrants(on ? [...rest, capability] : rest)
}
